import { useState, useEffect, Suspense } from "react";
import { lazyWithRetry as lazy } from "@/utils/lazyWithRetry";
import { queryKeys } from "@/api/queryKeys";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { fetchAuthStatus } from "@/api/client";
import TopBar from "@/components/layout/TopBar";
import MobileNav from "@/components/layout/MobileNav";
import AuthSetup from "@/components/features/auth/AuthSetup";
import LoginPage from "@/components/features/auth/LoginPage";
import ErrorBoundary from "@/components/ErrorBoundary";
import PageErrorBoundary from "@/components/PageErrorBoundary";
import HealthStrip from "@/components/shared/HealthStrip";
import PageLoader from "@/components/ui/PageLoader";
import NavProgressBar from "@/components/ui/NavProgressBar";

// Lazy-loaded pages — only fetched when navigated to
const TradeQueue = lazy(() => import("@/components/features/trade/TradeQueue"));
const ChatPanel = lazy(() => import("@/components/chat/ChatPanel"));
const SettingsPage = lazy(() => import("@/components/features/settings/SettingsPage"));
const PaperTradingPage = lazy(() => import("@/components/features/paper/PaperTradingPage"));
const CommandCenterPage = lazy(() => import("@/components/features/command-center/CommandCenterPage"));
const LabPage = lazy(() => import("@/components/features/lab/LabPage"));
const PnlPage = lazy(() => import("@/components/features/pnl/PnlPage"));
const Dashboard = lazy(() => import("@/components/features/dashboard/Dashboard"));
const SchedulerPage = lazy(() => import("@/components/features/scheduler/SchedulerPage"));
const LogsPage = lazy(() => import("@/components/features/logs/LogsPage"));
const SubscriptionsPage = lazy(() => import("@/components/features/subscriptions/SubscriptionsPage"));
const GTTPage = lazy(() => import("@/components/features/gtt/GTTPage"));
const ReplayPage = lazy(() => import("@/components/features/replay/ReplayPage"));
const ScanMonitorPage = lazy(() => import("@/components/features/scan-monitor/ScanMonitorPage"));
const MetricsDashboard = lazy(() => import("@/components/features/metrics/MetricsDashboard"));
// 2026-05-13 Phase 4 bulletproof: per-entry-strategy P&L explorer
const StrategyPnlPanel = lazy(() => import("@/components/features/admin/StrategyPnlPanel"));
import { useHashRouter } from "@/hooks/useHashRouter";
import { LiveTicksProvider } from "@/contexts/LiveTicksContext";
import { ToastProvider } from "@/contexts/ToastContext";
import StoreSync from "@/store/StoreSync";
import type { Page } from "@/types/trading";

import { queryClient } from "@/api/queryClient";
import { bootstrapState } from "@/state/bootstrap";

function AppShell({ onLogout }: { onLogout: () => void }) {
  const { page, tab, navigate, setTab } = useHashRouter();
  // No refetchInterval — auth_status is bootstrap-primed and pushed via
  // WS state_delta (mirrored into RQ cache by stateChannel.ts).
  const { data: auth } = useQuery({ queryKey: queryKeys.authStatus, queryFn: fetchAuthStatus });
  const [moreOpen, setMoreOpen] = useState(false);

  // 2026-04-24: bootstrap gates page rendering (only after auth). The state
  // store + React Query cache get populated from one bootstrap call BEFORE
  // any page component mounts → page-mount useQuery calls find primed
  // cache and skip per-component REST calls. Bounded to 2.5s so a slow
  // backend doesn't block forever; on timeout we render anyway and queries
  // fall back to individual fetches.
  const [bootstrapDone, setBootstrapDone] = useState(false);
  useEffect(() => {
    if (!auth?.authenticated) return;
    let cancelled = false;
    const timeout = setTimeout(() => {
      if (!cancelled) {
        console.warn("[App] bootstrap timeout — rendering with empty cache");
        setBootstrapDone(true);
      }
    }, 2500);
    void bootstrapState().finally(() => {
      if (!cancelled) {
        clearTimeout(timeout);
        setBootstrapDone(true);
      }
    });
    return () => { cancelled = true; clearTimeout(timeout); };
  }, [auth?.authenticated]);

  return (
    <div className="flex h-screen overflow-hidden bg-bg">
      <NavProgressBar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <TopBar page={page} setPage={(p: Page) => navigate(p)} onMenuClick={() => {}} onLogout={onLogout} />
        <HealthStrip />
        <main className="flex-1 overflow-hidden px-3 sm:px-6 pt-1 sm:pt-2 pb-[72px] md:pb-3 flex flex-col">
          {/* Tri-state render: auth-loading | auth-failed | auth-ok-bootstrapping | ready.
              Previously this used `auth?.authenticated && !bootstrapDone ? loader : pages`,
              but when `auth` is undefined (useQuery still loading) the condition is
              undefined → falsy → pages mounted prematurely. Then auth resolved → pages
              unmounted. Then bootstrap completed → pages mounted AGAIN. Every page-level
              useQuery fired twice. Now pages mount exactly once. */}
          {auth === undefined && <div className="mb-4 shrink-0"><PageLoader /></div>}
          {auth !== undefined && !auth.authenticated && <div className="mb-4 shrink-0"><AuthSetup /></div>}
          <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">
          {auth?.authenticated && !bootstrapDone ? (
            <PageLoader />
          ) : auth?.authenticated && bootstrapDone ? (
          <Suspense fallback={<PageLoader />}>
            {/* ── New consolidated pages ────────────────────────── */}
            {page === "home"     && <PageErrorBoundary page="Home"><CommandCenterPage initialTab={tab ?? undefined} onTabChange={setTab} setPage={(p: Page) => navigate(p)} /></PageErrorBoundary>}
            {page === "lab"      && <PageErrorBoundary page="Lab"><LabPage initialTab={tab ?? undefined} onTabChange={setTab} /></PageErrorBoundary>}
            {page === "pnl"      && <PageErrorBoundary page="P&L"><PnlPage initialTab={tab ?? undefined} onTabChange={setTab} /></PageErrorBoundary>}

            {/* ── Unchanged pages ───────────────────────────────── */}
            {page === "trade"    && <PageErrorBoundary page="Trade"><TradeQueue initialTab={tab ?? undefined} onTabChange={setTab} /></PageErrorBoundary>}
            {page === "paper"    && <PageErrorBoundary page="Paper Trading"><PaperTradingPage initialTab={tab ?? undefined} onTabChange={setTab} /></PageErrorBoundary>}
            {page === "chat"     && <PageErrorBoundary page="Chat"><ChatPanel /></PageErrorBoundary>}
            {page === "settings" && <PageErrorBoundary page="Settings"><SettingsPage initialTab={tab ?? undefined} onTabChange={setTab} /></PageErrorBoundary>}
            {page === "metrics"  && <PageErrorBoundary page="Metrics"><MetricsDashboard /></PageErrorBoundary>}
            {page === "replay"   && <PageErrorBoundary page="Replay"><ReplayPage initialTab={tab ?? undefined} onTabChange={setTab} /></PageErrorBoundary>}
            {page === "logs"     && <PageErrorBoundary page="Logs"><LogsPage /></PageErrorBoundary>}

            {/* ── Legacy page renders — active until Phase 6 cleanup ── */}
            {page === "dashboard"     && <PageErrorBoundary page="Dashboard"><Dashboard setPage={(p: Page) => navigate(p)} /></PageErrorBoundary>}
            {/* backtest page retired 2026-05-24 → useHashRouter redirects "#/backtest" to "#/lab/data"; the one backtest is the Replay page */}
            {page === "scheduler"     && <PageErrorBoundary page="Scheduler"><SchedulerPage /></PageErrorBoundary>}
            {page === "subscriptions" && <PageErrorBoundary page="Subscriptions"><SubscriptionsPage /></PageErrorBoundary>}
            {page === "gtt"           && <PageErrorBoundary page="GTT"><GTTPage initialTab={tab ?? undefined} onTabChange={setTab} /></PageErrorBoundary>}
            {page === "day-replay"    && <RedirectToReplayChart />}
            {page === "scan-monitor"  && <PageErrorBoundary page="Scan Monitor"><ScanMonitorPage /></PageErrorBoundary>}
            {page === "strategy-pnl"  && <PageErrorBoundary page="Strategy P&L"><StrategyPnlPanel /></PageErrorBoundary>}
          </Suspense>
          ) : null}
          </div>
        </main>
        <MobileNav page={page} setPage={(p: Page) => navigate(p)} moreOpen={moreOpen} setMoreOpen={setMoreOpen} />
      </div>
    </div>
  );
}

function AuthGate() {
  const [authed, setAuthed] = useState(() => !!localStorage.getItem("mct_token"));
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    const handler = () => { setSessionExpired(true); setAuthed(false); };
    window.addEventListener("mct:session-expired", handler);
    return () => window.removeEventListener("mct:session-expired", handler);
  }, []);

  if (!authed) {
    return (
      <div>
        {sessionExpired && (
          <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-yellow-950/90 border border-yellow-500/40 text-yellow-300 text-sm font-semibold px-5 py-2.5 rounded-xl shadow-lg">
            Session expired — please sign in again
          </div>
        )}
        <LoginPage onSuccess={() => { setSessionExpired(false); setAuthed(true); }} />
      </div>
    );
  }
  return (
    <LiveTicksProvider>
      <ToastProvider>
        <StoreSync />
        <AppShell onLogout={() => setAuthed(false)} />
      </ToastProvider>
    </LiveTicksProvider>
  );
}

/** Redirect the legacy #/day-replay page to #/replay/chart */
function RedirectToReplayChart() {
  useEffect(() => {
    window.location.hash = "#/replay/chart";
  }, []);
  return null;
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthGate />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
